import { useState } from 'react'
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { RiskWorkspace } from '@app/types'
import { WorkspaceActions } from './workspace-actions'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { WorkspaceDeleteDialog } from './workspace-delete-dialog'
import { WorkspaceInformationDialog } from './workspace-information-dialog'
import { riskDeleteWorkspaceAtom } from '@app/state/riskAtoms'
import { useAtom } from 'jotai'
import { toast } from '@app/components/ui/Toast/toast'
import { AxiosError } from 'axios'

type WorkspacesTableProps = {
  workspaces: RiskWorkspace[]
  selectedWorkspace: null | string
  setSelectedWorkspace: (arg: string) => void
}

const PAGE_SIZE_OPTIONS = [10, 25, 50]

export const WorkspacesTable = ({
  workspaces,
  selectedWorkspace,
  setSelectedWorkspace,
}: WorkspacesTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE_OPTIONS[0],
  })
  const [deleteDialog, setDeleteDialog] = useState<null | string>(null)
  const [informationDialog, setInformationDialog] = useState<null | string>(null)
  const [selectedDialogWorkspace, setSelectedDialogWorkspace] = useState<RiskWorkspace | null>(null)

  const [{ mutate: deleteWorkspace }] = useAtom(riskDeleteWorkspaceAtom)

  const COLUMNS: ColumnDef<RiskWorkspace>[] = [
    {
      accessorKey: 'name',
      header: 'WORKSPACE NAME',
      cell: ({ getValue, row }) => {
        const name = getValue<string>()
        const rowId = row.original.id

        return (
          <div onClick={() => setSelectedWorkspace(rowId)} className="py-4 cursor-pointer">
            <RadioGroup
              className="flex pl-2"
              value={selectedWorkspace}
              onValueChange={setSelectedWorkspace}
            >
              <RadioGroupItem
                value={rowId}
                id={name}
                className={`w-4 h-4 rounded-full border-2 border-gray-300 [&_svg]:h-2.5 [&_svg]:w-2.5 ${rowId === selectedWorkspace && 'border-primary'}`}
              />
              <label htmlFor={name} className="capitalize pl-2 cursor-pointer">
                {name}
              </label>
            </RadioGroup>
          </div>
        )
      },
    },
    {
      accessorKey: 'updated_at',
      header: 'LAST UPDATED',
      cell: ({ getValue }) => {
        const date = new Date(getValue<string>())
        const last_updated = new Intl.DateTimeFormat('en-NZ').format(date)

        return last_updated
      },
    },
    {
      accessorKey: 'author',
      header: 'AUTHOR',
      cell: ({ getValue }) => {
        const author = getValue<{ email: string }>().email

        return author
      },
    },
    // {
    //   accessorKey: 'tags',
    //   header: 'Damage Ratio',
    //   cell: ({ getValue }) => getValue<number>().toFixed(2),
    // },
    {
      header: 'ACTIONS',
      cell: ({ row }) => {
        return (
          <WorkspaceActions
            workspace={row.original}
            setDeleteDialog={setDeleteDialog}
            setInformationDialog={setInformationDialog}
            setSelectedDialogWorkspace={setSelectedDialogWorkspace}
          />
        )
      },
    },
  ]

  const table = useReactTable({
    data: workspaces ?? [],
    columns: COLUMNS,
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSortingRemoval: false,
  })

  const { pageSize } = table.getState().pagination
  const rowsShown = table.getRowModel().rows.length

  const handleDeleteConfirm = () => {
    if (!deleteDialog) return
    const workspaceToDelete = workspaces.find((workspace) => workspace.id === deleteDialog)
    if (!workspaceToDelete) return

    deleteWorkspace(deleteDialog, {
      onSuccess: () => {
        toast({
          title: (
            <span>
              <b>{workspaceToDelete.name}</b> has been deleted
            </span>
          ),
          duration: 3000,
          type: 'success',
          position: 'top-right',
        })
      },
      onError: (error: unknown) => {
        const errorMessage = error instanceof AxiosError ? error.response?.data?.error : undefined
        toast({
          title: (
            <span>
              Failed to delete Workspace: <b>{workspaceToDelete.name}</b>
            </span>
          ),
          description: errorMessage,
          duration: 4000,
          type: 'error',
          position: 'top-right',
        })
      },
    })
    setDeleteDialog(null)
  }

  const onCancelDialog = () => {
    setDeleteDialog(null)
    setInformationDialog(null)
  }

  if (!workspaces) {
    return 'loading...'
  }

  return (
    <div className="flex flex-col gap-3 mt-4">
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort()
                  const sortDir = header.column.getIsSorted()

                  return (
                    <TableHead
                      key={header.id}
                      className={`text-[12px] text-neutral-600 ${header.id === 'name' && 'pl-12'}`}
                    >
                      {canSort ? (
                        <button
                          className="flex items-center gap-1 hover:text-foreground"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sortDir === 'asc' && <ArrowUp className="h-3.5 w-3.5 flex-shrink-0" />}
                          {sortDir === 'desc' && (
                            <ArrowDown className="h-3.5 w-3.5 flex-shrink-0" />
                          )}
                          {sortDir === false && (
                            <ArrowUpDown className="h-3.5 w-3.5 opacity-50 flex-shrink-0" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="bg-white">
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} className="h-14">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter className="bg-white text-neutral-600 text-[12px]">
            <TableRow>
              <TableCell colSpan={4}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Select
                      value={String(pageSize)}
                      onValueChange={(value) => table.setPageSize(Number(value))}
                    >
                      <SelectTrigger className="h-8 w-[100px] border border-neutral-200 mr-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <SelectItem key={size} value={String(size)}>
                            <span className="text-[12px] text-neutral-800">Show {size}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="">
                      {rowsShown} of {workspaces.length} shown
                    </span>
                  </div>
                  <div className="flex items-center gap-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Prev
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      {deleteDialog && (
        <WorkspaceDeleteDialog
          open={Boolean(deleteDialog)}
          onOpenChange={onCancelDialog}
          workspace={selectedDialogWorkspace}
          onConfirm={handleDeleteConfirm}
        />
      )}
      {informationDialog && (
        <WorkspaceInformationDialog
          open={Boolean(informationDialog)}
          onOpenChange={onCancelDialog}
          workspace={selectedDialogWorkspace}
          onConfirm={() => {
            if (selectedDialogWorkspace) {
              setSelectedWorkspace(selectedDialogWorkspace.id)
            }
          }}
        />
      )}
    </div>
  )
}
